/**
 * Lightweight gtag event helpers.
 *
 * Ported from form720-ui/src/seo/track.js. The two products share no code, so
 * this is a deliberate copy - keep them in step.
 *
 * These are safe no-ops until <Analytics> has injected gtag.js (which only
 * happens when REACT_APP_GA4_MEASUREMENT_ID / REACT_APP_GOOGLE_ADS_ID are set).
 * So calling them in local dev or before analytics is configured does nothing.
 *
 * Google Ads conversion (for paid campaigns) fires only when BOTH are set:
 *   REACT_APP_GOOGLE_ADS_ID            e.g. "AW-1234567890"
 *   REACT_APP_GOOGLE_ADS_SIGNUP_LABEL  the conversion action's label
 */
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;
const ADS_SIGNUP_LABEL = process.env.REACT_APP_GOOGLE_ADS_SIGNUP_LABEL;
const ADS_PURCHASE_LABEL = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL;

export function track(event, params = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", event, params);
}

/** Fire a GA4 page_view for SPA route changes. */
export function trackPageView(path) {
  track("page_view", { page_path: path });
}

/**
 * Fire on a completed signup - the account was actually created (the API
 * returned a verification challenge), not merely that the button was clicked.
 *
 * This is the event that answers "did anyone sign up, and where did they come
 * from". Signup on 2290 is gated by Turnstile and then by an emailed OTP, so a
 * `sign_up` count that stays at zero while `page_view` climbs is the signature
 * of a blocked funnel rather than absent traffic - which is exactly the
 * question this instrumentation exists to settle.
 */
export function trackSignup(params = {}) {
  track("sign_up", { method: "email", ...params });
  if (ADS_ID && ADS_SIGNUP_LABEL) {
    track("conversion", { send_to: `${ADS_ID}/${ADS_SIGNUP_LABEL}` });
  }
}

/**
 * Fire when an emailed OTP is accepted and the account becomes usable.
 *
 * Signup and verification are SEPARATE conversions on 2290 because they can
 * fail independently: the row is created by POST /auth/signup, but the account
 * is unusable until the emailed code is entered. A healthy `sign_up` count
 * alongside a near-zero `email_verified` count means the OTP mail is not
 * arriving - a failure mode that is otherwise invisible from the outside.
 */
export function trackEmailVerified(params = {}) {
  track("email_verified", { method: "otp", ...params });
}

/**
 * Fire on a CONFIRMED service-fee payment - the only event here that is revenue.
 *
 * Call site is PaymentDetailsStep, after waitForFinal() returns neither FAILED
 * nor PENDING. Deliberately not fired on Stripe's `succeeded` alone: our backend
 * finalises the row afterwards, and a payment Stripe accepted can still land
 * FAILED. Reporting revenue we did not keep is worse than reporting none.
 *
 * `valueCents` is the TOTAL charged - service fee plus the $1.00 processing fee,
 * i.e. what we actually collect. It is NOT the Heavy Vehicle Use Tax. The HVUT
 * runs to hundreds or thousands per return and is IRS money passing through us;
 * sending it as conversion value would overstate revenue by roughly 100x and, if
 * value-based bidding is ever switched on, would make Google bid against income
 * that does not exist.
 *
 * `transactionId` is Stripe's PaymentIntent id. GA4 de-duplicates `purchase` on
 * transaction_id, so a refresh or a double-submit cannot double-count.
 *
 * In Google Ads this is the PRIMARY conversion; sign_up and email_verified are
 * secondary/observation-only. At the volumes a small test produces there will be
 * very few of these, which is exactly why the soft events are tracked too - they
 * are the only signal with enough volume to show WHERE the funnel leaks.
 */
export function trackPurchase({ transactionId, valueCents, returnId }) {
  const value = valueCents / 100;
  track("purchase", {
    transaction_id: transactionId,
    value,
    currency: "USD",
    return_id: returnId,
  });
  if (ADS_ID && ADS_PURCHASE_LABEL) {
    track("conversion", {
      send_to: `${ADS_ID}/${ADS_PURCHASE_LABEL}`,
      value,
      currency: "USD",
      transaction_id: transactionId,
    });
  }
}
